import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Phone, RefreshCw, Inbox, Loader2 } from "lucide-react";
import { apiClient, formatApiError, TOKEN_KEY } from "../lib/api";
import { toast } from "sonner";

const SUBJECT_LABELS = {
  komunikacyjne: "Komunikacyjne",
  zyciowe: "Na życie",
  majatkowe: "Majątkowe / Dom",
  nnw: "NNW",
  firmowe: "Firmowe",
  inne: "Inne",
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminDashboard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/submissions");
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || "Błąd ładowania zgłoszeń");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Panel agenta · Zgłoszenia";
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin/login", { replace: true });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunąć to zgłoszenie?")) return;
    try {
      await apiClient.delete(`/admin/submissions/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Zgłoszenie usunięte");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Błąd usuwania");
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-forest text-cream">
        <div className="container-narrow flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-mustard text-forest font-heading font-bold text-lg">
              JK
            </span>
            <div>
              <div className="font-heading font-semibold text-cream">Panel agenta</div>
              <div className="text-cream/60 text-xs">Zgłoszenia z formularza</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-cream/10 hover:bg-cream/15 text-cream text-sm font-medium transition-colors"
            >
              <RefreshCw size={15} />
              <span className="hidden sm:inline">Odśwież</span>
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-mustard hover:bg-mustard-dark text-white text-sm font-semibold transition-colors"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">Wyloguj</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container-narrow py-10 md:py-14">
        <div className="flex items-baseline justify-between mb-6">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-forest">
            Zgłoszenia
          </h1>
          <div className="text-ink-muted text-sm" data-testid="admin-submissions-count">
            {items.length} {items.length === 1 ? "zgłoszenie" : "zgłoszeń"}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-ink-muted">
            <Loader2 size={20} className="animate-spin mr-2" /> Ładowanie...
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="bg-white border border-bordersoft rounded-2xl p-12 text-center"
          >
            <Inbox size={36} className="mx-auto text-ink-muted mb-4" />
            <h3 className="font-heading text-xl font-semibold text-forest mb-2">
              Brak zgłoszeń
            </h3>
            <p className="text-ink-muted">
              Gdy ktoś wypełni formularz na stronie, zobaczysz to tutaj.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-bordersoft rounded-2xl overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-forest/5 text-xs uppercase tracking-wider font-semibold text-forest border-b border-bordersoft">
              <div className="col-span-2">Data</div>
              <div className="col-span-2">Imię</div>
              <div className="col-span-2">Telefon</div>
              <div className="col-span-2">Temat</div>
              <div className="col-span-3">Wiadomość</div>
              <div className="col-span-1 text-right">Akcje</div>
            </div>
            <ul className="divide-y divide-bordersoft">
              {items.map((s) => (
                <li
                  key={s.id}
                  data-testid={`admin-submission-${s.id}`}
                  className="px-6 py-5 md:grid md:grid-cols-12 md:gap-4 md:items-start hover:bg-cream/30 transition-colors"
                >
                  <div className="md:col-span-2 text-sm text-ink-muted md:text-base">
                    <div className="md:hidden text-xs uppercase font-semibold text-forest mb-1">Data</div>
                    {formatDate(s.created_at)}
                  </div>
                  <div className="md:col-span-2 mt-2 md:mt-0">
                    <div className="md:hidden text-xs uppercase font-semibold text-forest mb-1">Imię</div>
                    <div className="font-semibold text-forest">{s.name}</div>
                  </div>
                  <div className="md:col-span-2 mt-2 md:mt-0">
                    <div className="md:hidden text-xs uppercase font-semibold text-forest mb-1">Telefon</div>
                    <a
                      href={`tel:${s.phone}`}
                      className="inline-flex items-center gap-1.5 text-forest hover:text-mustard-dark font-medium"
                    >
                      <Phone size={14} />
                      {s.phone}
                    </a>
                  </div>
                  <div className="md:col-span-2 mt-2 md:mt-0">
                    <div className="md:hidden text-xs uppercase font-semibold text-forest mb-1">Temat</div>
                    <span className="inline-block px-2.5 py-1 rounded-full bg-mustard/15 text-mustard-dark text-xs font-semibold">
                      {SUBJECT_LABELS[s.subject] || s.subject}
                    </span>
                  </div>
                  <div className="md:col-span-3 mt-2 md:mt-0 text-ink text-sm md:text-base leading-relaxed whitespace-pre-wrap break-words">
                    <div className="md:hidden text-xs uppercase font-semibold text-forest mb-1">Wiadomość</div>
                    {s.message}
                  </div>
                  <div className="md:col-span-1 mt-3 md:mt-0 md:text-right">
                    <button
                      onClick={() => handleDelete(s.id)}
                      data-testid={`admin-delete-${s.id}`}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium"
                      aria-label="Usuń zgłoszenie"
                    >
                      <Trash2 size={15} />
                      <span className="md:hidden">Usuń</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
